/**
 * AddPhaseColumn - Coluna para adicionar nova fase ao Kanban
 * 
 * Usa Popover (Portal) para sobrepor o formulário sobre o container
 * com overflow:hidden do Kanban, evitando que seja cortado.
 * 
 * Versão: 1.1.0
 */

import { useState } from 'react';
import { Plus, Check, X, Loader2, Trophy, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Paleta de 20 cores organizadas em 4 linhas temáticas (padronizada com Kanban Conversas)
const PRESET_COLORS = [
  // Linha 1 - Cores primárias vibrantes
  '#3b82f6', '#22c55e', '#ef4444', '#f97316', '#eab308',
  // Linha 2 - Tons alternativos
  '#8b5cf6', '#ec4899', '#06b6d4', '#14b8a6', '#84cc16',
  // Linha 3 - Tons pastel/suaves
  '#60a5fa', '#4ade80', '#f87171', '#fb923c', '#facc15',
  // Linha 4 - Tons neutros e escuros
  '#a855f7', '#f472b6', '#6b7280', '#374151', '#1f2937',
];

interface AddPhaseColumnProps {
  onAdd: (name: string, color: string, probability: number, isWon: boolean, isLost: boolean) => Promise<void>;
  isLoading?: boolean;
  // v1.2.0 - Controle de visibilidade via permissão granular `crm_phases.can_create`
  // Default true preserva compatibilidade com chamadas existentes
  canCreate?: boolean;
}

/**
 * Componente de coluna para adicionar nova fase.
 * O formulário é renderizado via Popover Portal, sobrepondo o Kanban
 * independente do overflow:hidden dos containers ancestrais.
 */
export function AddPhaseColumn({ onAdd, isLoading = false, canCreate = true }: AddPhaseColumnProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [probability, setProbability] = useState(50);
  const [isWon, setIsWon] = useState(false);
  const [isLost, setIsLost] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handler para criar fase
  const handleCreate = async () => {
    if (!name.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await onAdd(name.trim(), color, probability, isWon, isLost);
      // Reset form e fecha popover
      setName('');
      setColor(PRESET_COLORS[0]);
      setProbability(50);
      setIsWon(false);
      setIsLost(false);
      setIsOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler para cancelar e resetar o formulário
  const handleCancel = () => {
    setName('');
    setColor(PRESET_COLORS[0]);
    setProbability(50);
    setIsWon(false);
    setIsLost(false);
    setIsOpen(false);
  };

  // Controla abertura/fechamento do Popover
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCancel();
    } else {
      setIsOpen(true);
    }
  };

  // v1.2.0 - Oculta coluna inteira (sem placeholder) quando usuário não tem permissão
  // Posicionado APÓS hooks para respeitar Rules of Hooks
  if (!canCreate) return null;

  return (
    <div className="w-[280px] shrink-0">
      {/* 
        Popover usa Portal internamente → renderiza no <body>
        Sobrepõe qualquer overflow:hidden dos ancestrais do Kanban
      */}
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full h-auto py-3 border-2 border-dashed',
              'hover:border-primary/50 hover:bg-primary/5',
              'transition-colors'
            )}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Fase
          </Button>
        </PopoverTrigger>

        {/* 
          PopoverContent renderizado via Portal no <body>
          Alinhado abaixo do botão, sobrepõe o Kanban completamente
        */}
        <PopoverContent
          className="w-[280px] p-3"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          {/* Input de nome */}
          <Input
            placeholder="Nome da fase..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') handleCancel();
            }}
            autoFocus
            className="mb-3"
          />

          {/* Seletor de cores - grid 5x4 */}
          <div className="grid grid-cols-5 gap-1.5 mb-3">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={cn(
                  'w-6 h-6 rounded-full transition-all',
                  color === c && 'ring-2 ring-offset-2 ring-primary'
                )}
                style={{ backgroundColor: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>

          {/* Switches de tipo (Ganho/Perdido) com auto-preenchimento de probabilidade */}
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex items-center justify-between gap-2 p-2 rounded-md border border-green-200/50 bg-green-50/30 dark:bg-green-950/20 dark:border-green-800/30">
              <div className="flex items-center gap-1.5">
                <Trophy className={cn("h-4 w-4", isWon ? "text-green-500" : "text-muted-foreground/40")} />
                <Label className="text-xs">Fase de Ganho</Label>
              </div>
              <Switch
                checked={isWon}
                onCheckedChange={(v) => {
                  setIsWon(v);
                  if (v) {
                    setIsLost(false);
                    setProbability(100); // Auto-preenche 100% para fase de ganho
                  }
                }}
                className="data-[state=checked]:bg-green-500"
              />
            </div>
            <div className="flex items-center justify-between gap-2 p-2 rounded-md border border-red-200/50 bg-red-50/30 dark:bg-red-950/20 dark:border-red-800/30">
              <div className="flex items-center gap-1.5">
                <XCircle className={cn("h-4 w-4", isLost ? "text-red-500" : "text-muted-foreground/40")} />
                <Label className="text-xs">Fase de Perda</Label>
              </div>
              <Switch
                checked={isLost}
                onCheckedChange={(v) => {
                  setIsLost(v);
                  if (v) {
                    setIsWon(false);
                    setProbability(0); // Auto-preenche 0% para fase de perda
                  }
                }}
                className="data-[state=checked]:bg-red-500"
              />
            </div>
          </div>

          {/* Campo de probabilidade */}
          <div className="flex items-center gap-2 mb-3">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Prob.</Label>
            <Input
              type="number"
              value={probability}
              onChange={(e) => setProbability(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
              min={0}
              max={100}
              className="w-16 h-7 text-sm"
            />
            <span className="text-xs text-muted-foreground">%</span>
          </div>

          {/* Botões de ação */}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={!name.trim() || isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Check className="h-4 w-4 mr-1" />
              )}
              Criar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
